import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronDown,
  FileText,
  Folder,
  FolderInput,
  FolderPlus,
  Pencil,
  Plus,
  Search,
  Tag,
  Trash2,
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { updateAccount } from '../../services/firestoreService';
import { CRM_OWNER_EXECUTIVE_ROLES } from '../../services/accessControlService';
import DocumentViewerModal from '../DocumentViewerModal';
import { getTemplateViewUrl } from '../../utils/hrTemplates';

export const DOCUMENT_DEPARTMENTS = ['General', 'Sales', 'Operations', 'Technology', 'Finance', 'Marketing', 'HR'];

export function normalizeDeptLabel(value) {
  return String(value ?? '').trim().toLowerCase();
}

/** Owner/Executive see all additional docs; Members (and other non-exec CRM users) only see docs whose department matches internal_users.team. */
export function userCanViewAdditionalDocument(docEntry, internalRole, internalTeam) {
  if (internalRole != null && CRM_OWNER_EXECUTIVE_ROLES.includes(internalRole)) return true;
  const team = normalizeDeptLabel(internalTeam);
  const dept = normalizeDeptLabel(docEntry?.department);
  if (!team || !dept) return false;
  return dept === team;
}


/**
 * Lead → client conversion flips `type` on the same account doc, so `customDocuments` /
 * `documentFolders` carry over on their own. Documents uploaded before the lead page shared
 * this collection UI have no department or visibility, which the collection uses for team
 * visibility — and the client portal treats a missing visibility as client-facing. Stamp the
 * defaults at conversion so nothing drops out of the CRM or leaks into the portal.
 *
 * @returns {Array|null} the normalized array, or null when every document was already complete.
 */
export function normalizeCollectionForConversion(documents) {
  if (!Array.isArray(documents) || documents.length === 0) return null;
  let changed = false;
  const next = documents.map((d) => {
    const department = d?.department || 'Sales';
    const visibility = d?.visibility || 'internal';
    const folderId = d?.folderId || '';
    if (department !== d?.department || visibility !== d?.visibility || folderId !== d?.folderId) {
      changed = true;
    }
    return { ...d, department, visibility, folderId };
  });
  return changed ? next : null;
}

const formatDocDate = (v) => {
  if (!v) return '—';
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

/**
 * The account document collection — one implementation shared by the CRM lead
 * page and the CRM client page so a lead's folders, departments, visibility and
 * files survive the convert-to-client flip untouched (both write the same
 * `customDocuments` / `documentFolders` fields on accounts/{id}).
 *
 * @param {{
 *   accountId: string,
 *   account: object,
 *   onAccountChange?: (patch: object) => void,
 *   storageFolder?: string,
 *   filePrefix?: string,
 * }} props
 */
export default function AccountDocumentsTab({
  accountId,
  account,
  onAccountChange,
  storageFolder = 'client-documents',
  filePrefix = 'client',
}) {
  const { currentUser } = useAuth();
  const [internalRole, setInternalRole] = useState(null);
  const [internalTeam, setInternalTeam] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [docSearch, setDocSearch] = useState('');
  const [docDeptFilter, setDocDeptFilter] = useState('all');
  const [currentDocFolderId, setCurrentDocFolderId] = useState(null);
  const [docFolderModal, setDocFolderModal] = useState(null); // null | { id: string|null }
  const [docFolderName, setDocFolderName] = useState('');
  const [docFolderDept, setDocFolderDept] = useState('');
  const [dragDocId, setDragDocId] = useState(null);
  const [dropDocFolderId, setDropDocFolderId] = useState(null);
  const [moveCustomDocId, setMoveCustomDocId] = useState(null);
  const [docViewer, setDocViewer] = useState(null); // { url, title }

  const [pendingUploadFile, setPendingUploadFile] = useState(null);
  const [pendingUploadName, setPendingUploadName] = useState('');
  const [pendingUploadDepartment, setPendingUploadDepartment] = useState('');
  const [showUploadVisibilityModal, setShowUploadVisibilityModal] = useState(false);

  const [selectedCustomDocument, setSelectedCustomDocument] = useState(null);
  const [showCustomDocumentModal, setShowCustomDocumentModal] = useState(false);
  const [editingDocumentName, setEditingDocumentName] = useState('');
  const [editingDocumentVisibility, setEditingDocumentVisibility] = useState('internal');
  const [editingDocumentDepartment, setEditingDocumentDepartment] = useState('');

  useEffect(() => {
    if (!currentUser?.uid) {
      setInternalRole(null);
      setInternalTeam(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'internal_users', currentUser.uid));
        if (!cancelled) {
          const data = snap.exists() ? snap.data() : null;
          setInternalRole(data?.role ?? null);
          setInternalTeam(data?.team ?? null);
        }
      } catch (e) {
        console.error('AccountDocumentsTab: internal role', e);
        if (!cancelled) {
          setInternalRole(null);
          setInternalTeam(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid]);

  const customDocuments = useMemo(
    () => (Array.isArray(account?.customDocuments) ? account.customDocuments : []),
    [account?.customDocuments]
  );
  const docFolders = useMemo(
    () => (Array.isArray(account?.documentFolders) ? account.documentFolders : []),
    [account?.documentFolders]
  );

  const canManageOrgWide = internalRole != null && CRM_OWNER_EXECUTIVE_ROLES.includes(internalRole);
  const canUpload = canManageOrgWide || !!internalTeam;

  const visibleCustomDocuments = useMemo(() => {
    if (customDocuments.length === 0) return [];
    return customDocuments.filter((d) => userCanViewAdditionalDocument(d, internalRole, internalTeam));
  }, [customDocuments, internalRole, internalTeam]);

  /** Single write path: patch the account doc, then let the parent page merge the same patch into its copy. */
  const persist = useCallback(
    async (patch) => {
      await updateAccount(accountId, patch);
      onAccountChange?.(patch);
    },
    [accountId, onAccountChange]
  );

  const persistCustomDocs = useCallback((nextDocs) => persist({ customDocuments: nextDocs }), [persist]);
  const persistDocFolders = useCallback((nextFolders) => persist({ documentFolders: nextFolders }), [persist]);

  /**
   * A document uploaded while the account was a lead still lives under `lead-documents/`, so the
   * stored path (or the download URL) wins over rebuilding one from this page's storage folder.
   * A file we can't find shouldn't block removing the row.
   */
  const deleteStoredFile = async (docEntry) => {
    const candidates = [
      docEntry?.storagePath,
      docEntry?.url,
      docEntry?.fileName ? `${storageFolder}/${docEntry.fileName}` : null,
    ].filter(Boolean);
    for (const target of candidates) {
      try {
        await deleteObject(ref(storage, target));
        return;
      } catch (e) {
        console.error('Failed to delete stored file', target, e);
      }
    }
  };

  const startUpload = () => {
    if (!canUpload) {
      alert('Your profile must have a team assigned to upload additional documents.');
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setPendingUploadFile(file);
      setPendingUploadName(file.name.replace(/\.[^/.]+$/, ''));
      setPendingUploadDepartment(canManageOrgWide ? 'Sales' : (internalTeam || ''));
      setShowUploadVisibilityModal(true);
    };
    input.click();
  };

  const handleCustomDocumentUpload = async (file, visibility = 'internal', department = '', displayName = '') => {
    if (!file) return;

    const trimmedName = (displayName || '').trim();
    if (!trimmedName) {
      alert('Please enter a document name.');
      return;
    }

    const effectiveDepartment = canManageOrgWide ? (department || null) : (internalTeam || null);
    if (!canManageOrgWide && !internalTeam) {
      alert('Your profile must have a team assigned to upload additional documents.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB.');
      return;
    }

    try {
      setUploading(true);
      setShowUploadVisibilityModal(false);
      setPendingUploadFile(null);
      setPendingUploadName('');

      const uploaderName = currentUser?.displayName || currentUser?.email || 'Unknown';
      const timestamp = Date.now();
      const extMatch = file.name.match(/\.([^/.]+)$/);
      const fileName = `${filePrefix}-${accountId}-custom-${timestamp}${extMatch ? `.${extMatch[1]}` : ''}`;
      const storagePath = `${storageFolder}/${fileName}`;
      const snapshot = await uploadBytes(ref(storage, storagePath), file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      const newDocument = {
        id: timestamp.toString(),
        name: trimmedName,
        url: downloadURL,
        uploader: uploaderName,
        uploadDate: new Date().toISOString(),
        fileName,
        storagePath,
        visibility,
        department: effectiveDepartment,
        folderId: currentDocFolderId || '',
      };

      await persistCustomDocs([...customDocuments, newDocument]);
    } catch (error) {
      console.error('Error uploading custom document:', error);
      alert('Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleCustomDocumentView = (docEntry) => {
    if (!userCanViewAdditionalDocument(docEntry, internalRole, internalTeam)) return;
    setSelectedCustomDocument(docEntry);
    setEditingDocumentName(docEntry.name);
    setEditingDocumentVisibility(docEntry.visibility || 'internal');
    setEditingDocumentDepartment(docEntry.department || '');
    setShowCustomDocumentModal(true);
  };

  const handleCustomDocumentDownload = (docEntry) => {
    if (!userCanViewAdditionalDocument(docEntry, internalRole, internalTeam)) return;
    window.open(docEntry.url, '_blank');
  };

  const handleCustomDocumentDelete = async (documentId) => {
    const documentToDelete = customDocuments.find((d) => d.id === documentId);
    if (!documentToDelete) return;
    if (!userCanViewAdditionalDocument(documentToDelete, internalRole, internalTeam)) return;
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await deleteStoredFile(documentToDelete);
      await persistCustomDocs(customDocuments.filter((d) => d.id !== documentId));

      if (selectedCustomDocument?.id === documentId) {
        setShowCustomDocumentModal(false);
        setSelectedCustomDocument(null);
      }
    } catch (error) {
      console.error('Error deleting custom document:', error);
      alert('Failed to delete document. Please try again.');
    }
  };

  const handleCustomDocumentSaveEdits = async () => {
    if (!selectedCustomDocument) return;
    if (!userCanViewAdditionalDocument(selectedCustomDocument, internalRole, internalTeam)) return;
    if (!editingDocumentName.trim()) {
      alert('Please enter a document name.');
      return;
    }

    try {
      const deptForSave = canManageOrgWide ? (editingDocumentDepartment || null) : (internalTeam || null);
      const nextDocs = customDocuments.map((d) =>
        d.id === selectedCustomDocument.id
          ? {
              ...d,
              name: editingDocumentName.trim(),
              visibility: editingDocumentVisibility,
              department: deptForSave,
            }
          : d
      );

      await persistCustomDocs(nextDocs);
      setSelectedCustomDocument({
        ...selectedCustomDocument,
        name: editingDocumentName.trim(),
        visibility: editingDocumentVisibility,
        department: deptForSave,
      });
    } catch (error) {
      console.error('Error saving document edits:', error);
      alert('Failed to save changes. Please try again.');
    }
  };

  const saveDocFolder = async () => {
    const name = docFolderName.trim();
    if (!name) return;
    const department = docFolderDept || '';
    const next = docFolderModal?.id
      ? docFolders.map((f) => (f.id === docFolderModal.id ? { ...f, name, department } : f))
      : [
          ...docFolders,
          {
            id: Date.now().toString(),
            name,
            department,
            createdByName: currentUser?.displayName || currentUser?.email || 'Unknown',
            createdAt: new Date().toISOString(),
          },
        ];
    setDocFolderModal(null);
    try {
      await persistDocFolders(next);
    } catch (err) {
      console.error('Failed to save folder', err);
      alert('Failed to save folder.');
    }
  };

  const handleDeleteDocFolder = async (folderId) => {
    if (!window.confirm('Delete this folder? Its documents move back to All files.')) return;
    const nextFolders = docFolders.filter((f) => f.id !== folderId);
    const nextDocs = customDocuments.map((d) => (d.folderId === folderId ? { ...d, folderId: '' } : d));
    if (currentDocFolderId === folderId) setCurrentDocFolderId(null);
    try {
      await persist({ documentFolders: nextFolders, customDocuments: nextDocs });
    } catch (err) {
      console.error('Failed to delete folder', err);
      alert('Failed to delete folder.');
    }
  };

  const moveCustomDocToFolder = async (docId, folderId) => {
    const next = customDocuments.map((d) => (d.id === docId ? { ...d, folderId } : d));
    setMoveCustomDocId(null);
    try {
      await persistCustomDocs(next);
    } catch (err) {
      console.error('Failed to move document', err);
      alert('Failed to move document.');
    }
  };

  const openCustomDocViewer = (docEntry) => {
    if (!userCanViewAdditionalDocument(docEntry, internalRole, internalTeam)) return;
    const url = getTemplateViewUrl({ fileUrl: docEntry.url, fileName: docEntry.fileName || docEntry.name });
    if (url) setDocViewer({ url, title: docEntry.name });
  };

  const inFolder = docFolders.find((f) => f.id === currentDocFolderId) || null;
  const q = docSearch.trim().toLowerCase();
  const deptActive = docDeptFilter !== 'all';
  // A search or department filter spans every folder; otherwise show the current folder's contents.
  const spanAll = !!q || deptActive;
  const visibleFolders = currentDocFolderId || spanAll ? [] : docFolders;
  // Newest upload first; docs without a parseable uploadDate fall back to reverse array (append) order.
  const uploadTime = (d) => {
    const t = new Date(d.uploadDate).getTime();
    return Number.isNaN(t) ? null : t;
  };
  const visibleDocs = visibleCustomDocuments
    .map((d, i) => ({ d, i }))
    .filter(({ d }) => {
      if (q && !(d.name || '').toLowerCase().includes(q)) return false;
      if (deptActive && normalizeDeptLabel(d.department) !== normalizeDeptLabel(docDeptFilter)) return false;
      if (spanAll) return true;
      return (d.folderId || '') === (currentDocFolderId || '');
    })
    .sort((a, b) => {
      const ta = uploadTime(a.d);
      const tb = uploadTime(b.d);
      if (ta != null && tb != null && ta !== tb) return tb - ta;
      return b.i - a.i;
    })
    .map(({ d }) => d);
  const docCountIn = (folderId) => visibleCustomDocuments.filter((d) => (d.folderId || '') === folderId).length;

  const cellSubtle = 'p-2.5 text-gray-600 dark:text-gray-400';
  const outlineBtn =
    'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700';
  const rowIconBtn =
    'p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700';
  const rowIconBtnDanger = 'p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950';

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={docSearch}
            onChange={(e) => setDocSearch(e.target.value)}
            placeholder="Search documents…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Tag className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <select
              value={docDeptFilter}
              onChange={(e) => setDocDeptFilter(e.target.value)}
              className="pl-7 pr-8 py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-500"
              title="Filter by department"
            >
              <option value="all">All departments</option>
              {DOCUMENT_DEPARTMENTS.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
          <button
            type="button"
            onClick={() => {
              setDocFolderModal({ id: null });
              setDocFolderName('');
              setDocFolderDept('');
            }}
            className={outlineBtn}
          >
            <FolderPlus className="w-4 h-4" /> New folder
          </button>
          {canUpload && (
            <button
              type="button"
              disabled={uploading}
              onClick={startUpload}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white"
            >
              <Plus className="w-4 h-4" /> {uploading ? 'Uploading…' : 'Add document'}
            </button>
          )}
        </div>
      </div>

      {inFolder && (
        <div className="flex items-center gap-1.5 text-sm mb-3">
          <button
            type="button"
            onClick={() => setCurrentDocFolderId(null)}
            onDragOver={(e) => {
              if (dragDocId && currentDocFolderId) {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              }
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (dragDocId && currentDocFolderId) moveCustomDocToFolder(dragDocId, '');
              setDragDocId(null);
              setDropDocFolderId(null);
            }}
            className={`text-red-600 dark:text-red-400 hover:underline font-medium ${
              dragDocId ? 'ring-1 ring-red-400 rounded px-1' : ''
            }`}
          >
            All files
          </button>
          <span className="text-gray-400">/</span>
          <span className="text-gray-900 dark:text-white font-medium">{inFolder.name}</span>
        </div>
      )}

      {visibleFolders.length === 0 && visibleDocs.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
          <FileText className="w-10 h-10 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-600 dark:text-gray-300">
            {q
              ? 'No documents match your search.'
              : deptActive
                ? 'No documents in this department.'
                : inFolder
                  ? 'This folder is empty.'
                  : 'No documents yet.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 text-left">
                <th className="p-2.5 font-semibold text-gray-700 dark:text-gray-200">Name</th>
                <th className="p-2.5 font-semibold text-gray-700 dark:text-gray-200 whitespace-nowrap">Department</th>
                <th className="p-2.5 font-semibold text-gray-700 dark:text-gray-200 whitespace-nowrap">Visibility</th>
                <th className="p-2.5 font-semibold text-gray-700 dark:text-gray-200 whitespace-nowrap">Uploaded by</th>
                <th className="p-2.5 font-semibold text-gray-700 dark:text-gray-200 whitespace-nowrap">Uploaded</th>
                <th className="p-2.5 w-28" aria-hidden />
              </tr>
            </thead>
            <tbody>
              {visibleFolders.map((folder) => (
                <tr
                  key={folder.id}
                  onClick={() => setCurrentDocFolderId(folder.id)}
                  onDragOver={(e) => {
                    if (dragDocId) {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                    }
                  }}
                  onDragEnter={() => dragDocId && setDropDocFolderId(folder.id)}
                  onDragLeave={() => setDropDocFolderId((prev) => (prev === folder.id ? null : prev))}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragDocId) moveCustomDocToFolder(dragDocId, folder.id);
                    setDragDocId(null);
                    setDropDocFolderId(null);
                  }}
                  className={`border-t border-gray-100 dark:border-gray-800 cursor-pointer ${
                    dropDocFolderId === folder.id
                      ? 'bg-red-50 dark:bg-red-950/40 ring-2 ring-inset ring-red-400'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-800/60'
                  }`}
                >
                  <td className="p-2.5">
                    <span className="inline-flex items-center gap-2 font-medium text-gray-900 dark:text-white pointer-events-none">
                      <Folder className="w-4 h-4 text-amber-500 shrink-0" />
                      {folder.name}
                      <span className="text-xs font-normal text-gray-400">({docCountIn(folder.id)})</span>
                    </span>
                  </td>
                  <td className={`${cellSubtle} whitespace-nowrap`}>
                    {folder.department ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                        {folder.department}
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>
                  <td className={cellSubtle}>—</td>
                  <td className={`${cellSubtle} whitespace-nowrap`}>{folder.createdByName || '—'}</td>
                  <td className={`${cellSubtle} whitespace-nowrap`}>{formatDocDate(folder.createdAt)}</td>
                  <td className="p-2.5">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDocFolderModal({ id: folder.id });
                          setDocFolderName(folder.name);
                          setDocFolderDept(folder.department || '');
                        }}
                        className={rowIconBtn}
                        title="Rename folder"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteDocFolder(folder.id);
                        }}
                        className={rowIconBtnDanger}
                        title="Delete folder"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {visibleDocs.map((docEntry) => (
                <tr
                  key={docEntry.id}
                  draggable
                  onDragStart={(e) => {
                    setDragDocId(docEntry.id);
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', docEntry.id);
                  }}
                  onDragEnd={() => {
                    setDragDocId(null);
                    setDropDocFolderId(null);
                  }}
                  onClick={() => openCustomDocViewer(docEntry)}
                  className={`border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/60 cursor-pointer ${
                    dragDocId === docEntry.id ? 'opacity-50' : ''
                  }`}
                >
                  <td className="p-2.5">
                    <span className="inline-flex items-center gap-2 font-medium text-gray-900 dark:text-white">
                      <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                      <span className="truncate max-w-[260px]">{docEntry.name}</span>
                    </span>
                  </td>
                  <td className={`${cellSubtle} whitespace-nowrap`}>
                    {docEntry.department ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                        {docEntry.department}
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>
                  <td className={`${cellSubtle} whitespace-nowrap`}>
                    {docEntry.visibility === 'client' ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                        Client
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        Internal
                      </span>
                    )}
                  </td>
                  <td className={`${cellSubtle} whitespace-nowrap`}>{docEntry.uploader || '—'}</td>
                  <td className={`${cellSubtle} whitespace-nowrap`}>{formatDocDate(docEntry.uploadDate)}</td>
                  <td className="p-2.5">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCustomDocumentView(docEntry);
                        }}
                        className={rowIconBtn}
                        title="Details, rename & department"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      {docFolders.length > 0 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMoveCustomDocId(docEntry.id);
                          }}
                          className={rowIconBtn}
                          title="Move to folder"
                        >
                          <FolderInput className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCustomDocumentDelete(docEntry.id);
                        }}
                        className={rowIconBtnDanger}
                        title="Delete document"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Upload: name, department, then visibility choice */}
      {showUploadVisibilityModal && pendingUploadFile && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Document Details</h3>
              <button
                onClick={() => {
                  setShowUploadVisibilityModal(false);
                  setPendingUploadFile(null);
                  setPendingUploadName('');
                  setPendingUploadDepartment('');
                }}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Uploading: <span className="font-medium text-gray-900 dark:text-white">{pendingUploadFile.name}</span>
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Document Name</label>
              <input
                type="text"
                value={pendingUploadName}
                onChange={(e) => setPendingUploadName(e.target.value)}
                placeholder="Enter a document name"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Department</label>
              {canManageOrgWide ? (
                <select
                  value={pendingUploadDepartment}
                  onChange={(e) => setPendingUploadDepartment(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select department...</option>
                  {DOCUMENT_DEPARTMENTS.map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white">
                  {internalTeam || '—'}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => handleCustomDocumentUpload(pendingUploadFile, 'internal', pendingUploadDepartment, pendingUploadName)}
                disabled={uploading || !pendingUploadName.trim()}
                className="flex flex-col items-center justify-center py-8 px-6 rounded-xl border-2 border-gray-300 dark:border-gray-600 hover:border-gray-500 dark:hover:border-gray-500 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-12 h-12 text-gray-600 dark:text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span className="text-lg font-semibold text-gray-900 dark:text-white">Internal Only</span>
                <span className="text-sm text-gray-600 dark:text-gray-400 mt-1">Visible to team only</span>
              </button>
              <button
                onClick={() => handleCustomDocumentUpload(pendingUploadFile, 'client', pendingUploadDepartment, pendingUploadName)}
                disabled={uploading || !pendingUploadName.trim()}
                className="flex flex-col items-center justify-center py-8 px-6 rounded-xl border-2 border-blue-300 dark:border-blue-600 hover:border-blue-500 dark:hover:border-blue-500 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-12 h-12 text-blue-600 dark:text-blue-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <span className="text-lg font-semibold text-blue-900 dark:text-blue-100">Client Facing</span>
                <span className="text-sm text-blue-700 dark:text-blue-300 mt-1">Visible to client in portal</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document details: rename, department, visibility */}
      {showCustomDocumentModal && selectedCustomDocument && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <div className="flex-1" />
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white text-center flex-1">Document Details</h3>
              <div className="flex-1 flex justify-end">
                <button
                  onClick={() => {
                    setShowCustomDocumentModal(false);
                    setSelectedCustomDocument(null);
                    setEditingDocumentName('');
                    setEditingDocumentVisibility('internal');
                    setEditingDocumentDepartment('');
                  }}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Document Name</label>
                <input
                  type="text"
                  value={editingDocumentName}
                  onChange={(e) => setEditingDocumentName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter document name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Uploader</label>
                <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-md text-gray-900 dark:text-white">
                  {selectedCustomDocument.uploader || 'Unknown'}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Upload Date</label>
                <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-md text-gray-900 dark:text-white">
                  {formatDocDate(selectedCustomDocument.uploadDate)}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Department</label>
                {canManageOrgWide ? (
                  <select
                    value={editingDocumentDepartment}
                    onChange={(e) => setEditingDocumentDepartment(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select department...</option>
                    {DOCUMENT_DEPARTMENTS.map((dept) => (
                      <option key={dept} value={dept}>
                        {dept}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-md text-gray-900 dark:text-white">
                    {internalTeam || selectedCustomDocument.department || '—'}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Document Visibility</label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setEditingDocumentVisibility('internal')}
                    className={`flex flex-col items-center justify-center py-6 px-4 rounded-xl border-2 transition-all duration-200 ${
                      editingDocumentVisibility === 'internal'
                        ? 'border-green-500 dark:border-green-500 bg-green-100 dark:bg-green-900/50 ring-2 ring-green-400 dark:ring-green-500'
                        : 'border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    <svg
                      className={`w-10 h-10 mb-2 ${editingDocumentVisibility === 'internal' ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <span
                      className={`text-base font-semibold ${editingDocumentVisibility === 'internal' ? 'text-green-800 dark:text-green-200' : 'text-gray-600 dark:text-gray-400'}`}
                    >
                      Internal Only
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingDocumentVisibility('client')}
                    className={`flex flex-col items-center justify-center py-6 px-4 rounded-xl border-2 transition-all duration-200 ${
                      editingDocumentVisibility === 'client'
                        ? 'border-green-500 dark:border-green-500 bg-green-100 dark:bg-green-900/50 ring-2 ring-green-400 dark:ring-green-500'
                        : 'border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    <svg
                      className={`w-10 h-10 mb-2 ${editingDocumentVisibility === 'client' ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                    <span
                      className={`text-base font-semibold ${editingDocumentVisibility === 'client' ? 'text-green-800 dark:text-green-200' : 'text-gray-600 dark:text-gray-400'}`}
                    >
                      Client Facing
                    </span>
                  </button>
                </div>
              </div>

              <div className="pt-2">
                <button
                  onClick={handleCustomDocumentSaveEdits}
                  className="w-full px-4 py-3 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors duration-200"
                >
                  Save Changes
                </button>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => window.open(selectedCustomDocument.url, '_blank')}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors duration-200"
                >
                  View
                </button>
                <button
                  onClick={() => handleCustomDocumentDownload(selectedCustomDocument)}
                  className="flex-1 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors duration-200"
                >
                  Download
                </button>
                <button
                  onClick={() => handleCustomDocumentDelete(selectedCustomDocument.id)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors duration-200"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen document viewer */}
      <DocumentViewerModal
        isOpen={!!docViewer}
        onClose={() => setDocViewer(null)}
        url={docViewer?.url}
        title={docViewer?.title}
      />

      {/* New / rename folder */}
      {docFolderModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50" onClick={() => setDocFolderModal(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {docFolderModal.id ? 'Rename folder' : 'New folder'}
            </h3>
            <input
              autoFocus
              value={docFolderName}
              onChange={(e) => setDocFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  saveDocFolder();
                }
              }}
              placeholder="Folder name"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
            />
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-4 mb-1">Department</label>
            <select
              value={docFolderDept}
              onChange={(e) => setDocFolderDept(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">No department</option>
              {DOCUMENT_DEPARTMENTS.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => setDocFolderModal(null)}
                className="px-4 py-2 rounded-md text-sm bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveDocFolder}
                disabled={!docFolderName.trim()}
                className="px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {docFolderModal.id ? 'Save' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move a document to a folder */}
      {moveCustomDocId && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50" onClick={() => setMoveCustomDocId(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const moveDoc = customDocuments.find((d) => d.id === moveCustomDocId);
              const folderOption =
                'w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-default text-gray-900 dark:text-white';
              return (
                <>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Move to folder</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 truncate">{moveDoc?.name}</p>
                  <div className="space-y-1">
                    <button
                      type="button"
                      disabled={!(moveDoc?.folderId || '')}
                      onClick={() => moveDoc && moveCustomDocToFolder(moveDoc.id, '')}
                      className={folderOption}
                    >
                      <FileText className="w-4 h-4 text-gray-400" /> All files (no folder)
                    </button>
                    {docFolders.map((f) => (
                      <button
                        key={f.id}
                        type="button"
                        disabled={moveDoc?.folderId === f.id}
                        onClick={() => moveDoc && moveCustomDocToFolder(moveDoc.id, f.id)}
                        className={folderOption}
                      >
                        <Folder className="w-4 h-4 text-amber-500" /> {f.name}
                      </button>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
